import {
  useProjectId,
  useServiceName,
  useBuildingPackageId,
  useGetBuildingPackageResultsQuery,
  useBuildingPackage,
} from '@consigli/hooks';
import { BuildingPackageWorkflowStep } from '@consigli/types';
import { useMemo, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router';

import { Breadcrumb } from '@/features/breadcrumb/breadcrumb';
import { DocumentListProvider } from '@/features/document-list/document-list-context';
import { Route } from '@/routes';

export const BuildingPackageDetailWrapper = () => {
  const projectId = useProjectId();
  const buildingPackageId = useBuildingPackageId();
  const serviceName = useServiceName();
  const navigate = useNavigate();
  const location = useLocation();

  const { buildingPackage } = useBuildingPackage(projectId, buildingPackageId);
  const { data: buildingPackageResults } = useGetBuildingPackageResultsQuery({
    projectId,
    buildingPackageId,
    page: 'all',
  });

  const hasResults = useMemo(() => {
    return buildingPackageResults && buildingPackageResults.results
      ? buildingPackageResults.results.length > 0
      : false;
  }, [buildingPackageResults]);

  const workflowStep = useMemo(() => {
    return buildingPackage?.workflowStep;
  }, [buildingPackage]);

  const navRoute = useMemo(() => {
    if (workflowStep === BuildingPackageWorkflowStep.PROCESSING_SPEC_REQUIREMENTS_STEP) {
      return `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${buildingPackageId}/${Route.PROCESSING_SPEC_REQUIREMENTS}`;
    } else if (workflowStep === BuildingPackageWorkflowStep.USER_REVIEW_SPEC_REQUIREMENTS_STEP) {
      return `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${buildingPackageId}/${Route.USER_REVIEW_SPEC_REQUIREMENTS}`;
    } else if (workflowStep === BuildingPackageWorkflowStep.PROCESSING_LOAD_CALCULATION_STEP) {
      return `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${buildingPackageId}/${Route.PROCESSING_LOAD_CALCULATION}`;
    } else if (workflowStep === BuildingPackageWorkflowStep.PUBLISHED_LOAD_CALCULATION_STEP) {
      return `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${buildingPackageId}/${hasResults ? Route.RESULTS : Route.INPUT}`;
    }
    // Default fallback route
    return `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${buildingPackageId}/${hasResults ? Route.RESULTS : Route.INPUT}`;
  }, [projectId, serviceName, buildingPackageId, hasResults, workflowStep]);

  // Auto-navigate based on workflow step, but allow manual navigation to input/results
  useEffect(() => {
    if (!navRoute) return;

    const currentPath = location.pathname;
    const isOnInputOrResults =
      currentPath.includes(`/${Route.INPUT}`) || currentPath.includes(`/${Route.RESULTS}`);

    // Only auto-redirect if:
    // 1. We're in a processing/review step that requires a specific route, OR
    // 2. We're in published step but not on input/results pages
    const shouldAutoRedirect =
      workflowStep === BuildingPackageWorkflowStep.PROCESSING_SPEC_REQUIREMENTS_STEP ||
      workflowStep === BuildingPackageWorkflowStep.USER_REVIEW_SPEC_REQUIREMENTS_STEP ||
      workflowStep === BuildingPackageWorkflowStep.PROCESSING_LOAD_CALCULATION_STEP ||
      (workflowStep === BuildingPackageWorkflowStep.PUBLISHED_LOAD_CALCULATION_STEP &&
        !isOnInputOrResults);

    if (shouldAutoRedirect && currentPath !== navRoute) {
      navigate(navRoute, { replace: true });
    }
  }, [navRoute, location.pathname, navigate, workflowStep]);

  return (
    <>
      <Breadcrumb label={buildingPackage?.name || ''} to={navRoute} />
      <DocumentListProvider>
        <Outlet />
      </DocumentListProvider>
    </>
  );
};
